
import { createAsyncThunk } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/nextjs";
import { Auth } from "aws-amplify";
import axios from "axios";
import { EEATResult, EEATScrapeResult } from "./types";



// For EEAT calculator - placeholder URL, will be replaced later
const eeatCalculatorUrl = process.env.NEXT_PUBLIC_EEAT_CALCULATOR_LAMBDA_URL
const eeatDataUrl = process.env.NEXT_PUBLIC_EEAT_DATA_LAMBDA_URL

// The public E-E-A-T page (pages/EEAT.tsx) is used logged out, often inside an
// iframe on seowind.io. Logged-out visitors have no credentials that may invoke the
// main, signed Function URLs, so the page calls daily-capped public copies of the
// scraper and calculator, unsigned. A copy answers 429 with a message once its day
// is used up (eeat-calculator and scraper daily_limit.py in SEOwind_functions).
const scraperPublicUrl = process.env.NEXT_PUBLIC_SCRAPER_PUBLIC_LAMBDA_URL
const eeatCalculatorPublicUrl = process.env.NEXT_PUBLIC_EEAT_CALCULATOR_PUBLIC_LAMBDA_URL

function requirePublicUrl(url: string | undefined, name: string): string {
    if (!url) throw new Error(`${name} is not set`);
    return url;
}

// The message a public copy sends with its 429, or null for any other failure.
function dailyLimitMessage(error: any): string | null {
    const response = error?.response;
    if (response?.status !== 429) return null;
    return response.data?.message || "Today's free checks are used up. Please try again tomorrow.";
}

// Scrape website to get article text (public E-E-A-T page only)
export const scrapeArticle = createAsyncThunk<EEATScrapeResult, string>(
    "eeat/scrapeArticle",
    async (url: string, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                requirePublicUrl(scraperPublicUrl, "NEXT_PUBLIC_SCRAPER_PUBLIC_LAMBDA_URL"),
                { url, output_format: "markdown", get_metadata: true },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const result = response.data as EEATScrapeResult;

            // Transform new format to legacy format for backward compatibility
            if (result.data && Array.isArray(result.data)) {
                result.article_text = result.data[0] || "";
                result.article_title = result.data[1] || "";
                result.article_meta_description = result.data[2] || "";
            }

            return result;
        } catch (error) {
            const limitMessage = dailyLimitMessage(error);
            if (!limitMessage) {
                console.error("Error calling Lambda scraper function:", error);
                Sentry.captureException(error);
            }
            return rejectWithValue({
                success: false,
                error: limitMessage || "Failed to scrape article"
            });
        }
    }
);

// Calculate EEAT score from article text. briefId is optional: when the score
// is for a SEOwind article, it attributes the calculator's AI-cost line to it.
// publicCheck sends it to the public calculator copy instead (public E-E-A-T page).
export const calculateEEATScore = createAsyncThunk<EEATResult, { article_text: string, article_link: string, article_title: string, article_meta_description: string, briefId?: string, publicCheck?: boolean }>(
    "eeat/calculateScore",
    async ({ article_text, article_link, article_title, article_meta_description, briefId, publicCheck }: { article_text: string, article_link: string, article_title: string, article_meta_description: string, briefId?: string, publicCheck?: boolean }, { rejectWithValue }) => {
        // One payload for signing and sending: SigV4 fails unless the bytes match.
        const payload = { article_text, article_link, article_title, article_meta_description, ...(briefId ? { briefId } : {}) };
        try {
            if (publicCheck) {
                const response = await axios.post(
                    requirePublicUrl(eeatCalculatorPublicUrl, "NEXT_PUBLIC_EEAT_CALCULATOR_PUBLIC_LAMBDA_URL"),
                    payload,
                    { headers: { 'Content-Type': 'application/json' } }
                );
                return response.data as EEATResult;
            }

            // Check if AWS IAM auth is required
            if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
                try {
                    // Make sure these packages are imported
                    const { SignatureV4 } = require('@aws-sdk/signature-v4');
                    const { Sha256 } = require('@aws-crypto/sha256-browser');
                    const { HttpRequest } = require('@aws-sdk/protocol-http');

                    // Parse the Lambda Function URL
                    const lambdaUrl = new URL(eeatCalculatorUrl);
                    // Get temporary credentials from Cognito Identity Pool
                    const credentials = await Auth.currentCredentials();

                    // Extract potential function name from Function URL
                    const functionUrlId = lambdaUrl.hostname.split('.')[0];

                    // Create request object
                    const request = new HttpRequest({
                        method: 'POST',
                        hostname: lambdaUrl.hostname,
                        path: lambdaUrl.pathname,
                        headers: {
                            'Content-Type': 'application/json',
                            'host': lambdaUrl.hostname, // Required for signing
                        },
                        body: JSON.stringify(payload),
                    });

                    // Create signer
                    const signer = new SignatureV4({
                        credentials,
                        region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
                        service: 'lambda',
                        sha256: Sha256,
                    });

                    // Sign the request
                    const signedRequest = await signer.sign(request);

                    // Remove host header from signed headers for browser compatibility
                    const { host, ...headersForRequest } = signedRequest.headers;

                    // Make the request with the signed headers (excluding host)
                    const response = await axios({
                        url: eeatCalculatorUrl,
                        method: 'POST',
                        headers: headersForRequest,
                        data: JSON.stringify(payload),
                    });

                    return response.data as EEATResult;
                } catch (signingError) {
                    console.error("Error signing request:", signingError);
                    Sentry.captureException(signingError);
                    return rejectWithValue({
                        scores: { experience: 0, expertise: 0, authoritativeness: 0, trustworthiness: 0 },
                        suggestions: [],
                        overallScore: 0
                    });
                }
            } else {
                // If not using AWS_IAM auth, make a regular request
                const response = await axios.post(eeatCalculatorUrl, payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                return response.data as EEATResult;
            }
        } catch (error) {
            const limitMessage = dailyLimitMessage(error);
            if (!limitMessage) {
                console.error("Error calling EEAT calculator function:", error);
                Sentry.captureException(error);
            }
            return rejectWithValue({
                scores: { experience: 0, expertise: 0, authoritativeness: 0, trustworthiness: 0 },
                suggestions: [],
                overallScore: 0,
                error: limitMessage || "Failed to calculate EEAT score"
            });
        }
    }
);

// Get EEAT score from lamda endpoint using article id
export const getEEATScore = createAsyncThunk<EEATResult, string>(
    "eeat/getEEATScore",
    async (id: string, { rejectWithValue }) => {
        try {
            // Check if AWS IAM auth is required
            if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
                try {
                    // Make sure these packages are imported
                    const { SignatureV4 } = require('@aws-sdk/signature-v4');
                    const { Sha256 } = require('@aws-crypto/sha256-browser');
                    const { HttpRequest } = require('@aws-sdk/protocol-http');

                    // Parse the Lambda Function URL
                    const lambdaUrl = new URL(eeatDataUrl);
                    // Get temporary credentials from Cognito Identity Pool
                    const credentials = await Auth.currentCredentials();

                    // Extract potential function name from Function URL
                    const functionUrlId = lambdaUrl.hostname.split('.')[0];
                    // Create request object
                    const request = new HttpRequest({
                        method: 'POST',
                        hostname: lambdaUrl.hostname,
                        path: lambdaUrl.pathname,
                        headers: {
                            'Content-Type': 'application/json',
                            'host': lambdaUrl.hostname, // Required for signing
                        },
                        body: JSON.stringify({ article_id: id }),
                    });

                    // Create signer
                    const signer = new SignatureV4({
                        credentials,
                        region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
                        service: 'lambda',
                        sha256: Sha256,
                    });

                    // Sign the request
                    const signedRequest = await signer.sign(request);

                    // Remove host header from signed headers for browser compatibility
                    const { host, ...headersForRequest } = signedRequest.headers;

                    // Make the request with the signed headers (excluding host)
                    const response = await axios({
                        url: eeatDataUrl,
                        method: 'POST',
                        headers: headersForRequest,
                        data: JSON.stringify({ article_id: id }),
                    });

                    return response.data as EEATResult;
                } catch (signingError) {
                    console.error("Error signing request:", signingError);
                    Sentry.captureException(signingError);
                    return rejectWithValue({
                        scores: { experience: 0, expertise: 0, authoritativeness: 0, trustworthiness: 0 },
                        suggestions: [],
                        overallScore: 0
                    });
                }
            } else {
                // If not using AWS_IAM auth, make a regular request
                const response = await axios.post(eeatDataUrl, { article_id: id }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                return response.data as EEATResult;
            }
        } catch (error) {
            console.error("Error calling EEAT calculator function:", error);
            Sentry.captureException(error);
            return rejectWithValue({
                scores: { experience: 0, expertise: 0, authoritativeness: 0, trustworthiness: 0 },
                suggestions: [],
                overallScore: 0
            });
        }
    }
);